import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Axios } from "../../../Api/axios";
import {
  ADMIN_ONE_INTENSIVE,
  ADMIN_ADD_LECTURE_INTENSIVE,
  ADMIN_LECTURES_BY_INTENSIVE,
  ADMIN_LECTURE_INTENSIVE_BY_ID,
  ADMIN_LECTURE_INTENSIVE_FILE,
} from "../../../Api/Api";
import LoadingSubmit from "../../../Components/Loading/Loading";
import "../dashboard.css";
import "./IntensiveLectures.css";

const SLOTS = [
  { key: "pdf",   label: "ملف PDF",      uploadHint: "اضغط لرفع PDF",       icon: "📄", color: "#3b82f6", bg: "#eff6ff", accept: ".pdf,application/pdf" },
  { key: "video", label: "ملف الفيديو",  uploadHint: "اضغط لرفع الفيديو",  icon: "🎬", color: "#8b5cf6", bg: "#f5f3ff", accept: "video/*" },
  { key: "exam",  label: "ملف الاختبار", uploadHint: "اضغط لرفع الاختبار", icon: "📝", color: "#f59e0b", bg: "#fffbeb", accept: ".pdf,application/pdf,.doc,.docx" },
];

/* ─── URL validators ─── */
function isYouTubeUrl(url) {
  return /^https?:\/\/(www\.|m\.)?youtube\.com\/|^https?:\/\/youtu\.be\//i.test(url);
}

function isDriveUrl(url) {
  return /^https?:\/\/(drive|docs)\.google\.com\//i.test(url);
}

function validateSlotUrl(key, url) {
  if (!url || !url.trim()) return null; // empty is ok (optional)
  if (key === "video" && !isYouTubeUrl(url))
    return "رابط الفيديو يجب أن يكون من YouTube (youtube.com / youtu.be / m.youtube.com)";
  if ((key === "pdf" || key === "exam") && !isDriveUrl(url))
    return "رابط الملف يجب أن يكون من Google Drive (drive.google.com)";
  return null;
}

function getYouTubeEmbedUrl(url) {
  // youtu.be/ID
  const short = url.match(/youtu\.be\/([^?&]+)/);
  if (short) return `https://www.youtube.com/embed/${short[1]}`;
  // youtube.com/watch?v=ID
  const watch = url.match(/[?&]v=([^&]+)/);
  if (watch) return `https://www.youtube.com/embed/${watch[1]}`;
  // already embed
  if (url.includes('/embed/')) return url;
  return url;
}

/* ─── Open uploaded file via blob ─── */
async function openIntensiveFile(filename) {
  try {
    const res = await Axios.get(ADMIN_LECTURE_INTENSIVE_FILE(filename), { responseType: "blob" });
    const mimeType = res.headers["content-type"] || "application/octet-stream";
    const blob = new Blob([res.data], { type: mimeType });
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
  } catch {
    alert("تعذر فتح الملف");
  }
}

/* ─── Video Modal (YouTube embed or blob) ─── */
function VideoModal({ url, onClose }) {
  const isYT    = isYouTubeUrl(url);
  const embedUrl = isYT ? getYouTubeEmbedUrl(url) : null;
  const videoRef = useRef(null);

  function seek(seconds) {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime + seconds);
  }

  return (
    <div className="il-overlay" onClick={onClose}>
      <div className="il-video-modal" onClick={(e) => e.stopPropagation()}>
        <div className="il-video-header">
          <span>🎬 مشاهدة الفيديو</span>
          <button className="il-video-close" onClick={onClose}>✕</button>
        </div>

        {isYT ? (
          <iframe
            className="il-video-frame"
            src={embedUrl}
            title="YouTube video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <>
            <video ref={videoRef} className="il-video-frame" controls autoPlay>
              <source src={url} />
              المتصفح لا يدعم تشغيل الفيديو
            </video>
            <div className="il-video-seek-bar">
              <button className="il-seek-btn" onClick={() => seek(-10)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                </svg>
                <span>10-</span>
              </button>
              <button className="il-seek-btn" onClick={() => seek(10)}>
                <span>10+</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ transform: "scaleX(-1)" }}>
                  <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                </svg>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Delete confirm modal ─── */
function DeleteModal({ onConfirm, onCancel, deleting }) {
  return (
    <div className="il-overlay" onClick={!deleting ? onCancel : undefined}>
      <div className="il-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="il-confirm-icon">
          <svg width="28" height="28" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
          </svg>
        </div>
        <h3 className="il-confirm-title">تأكيد الحذف</h3>
        <p className="il-confirm-msg">هل تريد حذف هذه المحاضرة؟<br /><span className="il-confirm-warn">لا يمكن التراجع عن هذا الإجراء</span></p>
        <div className="il-confirm-actions">
          <button className="il-confirm-cancel" onClick={onCancel} disabled={deleting}>إلغاء</button>
          <button className="il-confirm-delete" onClick={onConfirm} disabled={deleting}>
            {deleting ? "جاري الحذف..." : "حذف"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Edit Modal ─── */
function EditModal({ lecture, onSave, onClose }) {
  const [editName, setEditName] = useState(lecture.name || "");
  const [files,   setFiles]    = useState({ pdf: null, video: null, exam: null });
  // cleared[key]=true means user explicitly removed the existing value
  const [cleared, setCleared]  = useState({ pdf: false, video: false, exam: false });
  const [texts,   setTexts]    = useState({
    pdf:   lecture.pdf   ? (/^https?:\/\//i.test(lecture.pdf)   ? lecture.pdf   : "") : "",
    video: lecture.video ? (/^https?:\/\//i.test(lecture.video) ? lecture.video : "") : "",
    exam:  lecture.exam  ? (/^https?:\/\//i.test(lecture.exam)  ? lecture.exam  : "") : "",
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");

  const hasUrlError = SLOTS.some((s) => !files[s.key] && !!validateSlotUrl(s.key, texts[s.key]));

  function clearSlot(key) {
    setFiles(p => ({ ...p, [key]: null }));
    setTexts(p => ({ ...p, [key]: "" }));
    setCleared(p => ({ ...p, [key]: true }));
  }

  async function handleSave() {
    if (hasUrlError) return;
    setSaving(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("name", editName.trim());
      SLOTS.forEach((s) => {
        if (files[s.key])            fd.append(s.key, files[s.key]);       // new file
        else if (texts[s.key].trim()) fd.append(s.key, texts[s.key].trim()); // new URL
        else if (cleared[s.key])      fd.append(s.key, "");                 // explicitly cleared
        // else: no change → don't send → backend keeps existing value
      });
      const res = await Axios.put(ADMIN_LECTURE_INTENSIVE_BY_ID(lecture.id), fd);
      onSave(res.data.lecture || lecture);
    } catch (e) {
      const msg    = e?.response?.data?.message || "";
      const detail = e?.response?.data?.Error   || e?.response?.data?.error || "";
      const status = e?.response?.status;
      setErr(
        detail
          ? `${msg} — ${detail}`
          : msg || `خطأ ${status || ""}: حدث خطأ أثناء التعديل`
      );
      setSaving(false);
    }
  }

  return (
    <div className="il-overlay" onClick={!saving ? onClose : undefined}>
      <div className="il-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="il-edit-header">
          <span>✏️ تعديل المحاضرة #{lecture.id}</span>
          <button className="il-video-close" onClick={onClose} disabled={saving}>✕</button>
        </div>
        {err && <div className="il-error" style={{ margin: "0 20px 0" }}>{err}</div>}
        <div className="il-name-field" style={{ padding: "14px 20px 0" }}>
          <label className="il-label">اسم الدرس <span className="il-optional">(اختياري)</span></label>
          <input type="text" className="il-url-input"
            placeholder="مثال: الدرس الأول - المقدمة..."
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            disabled={saving}
          />
        </div>
        <div className="il-fields-grid">
          {SLOTS.map((slot) => {
            const existing = lecture[slot.key];
            const isExistingUrl = existing && /^https?:\/\//i.test(existing);
            return (
              <div key={slot.key} className="il-field">
                <label className="il-label">{slot.label} <span className="il-optional">(اختياري)</span></label>

                {/* Show existing */}
                {existing && !files[slot.key] && !cleared[slot.key] && (
                  <div className="il-edit-current" style={{ borderColor: slot.color, background: slot.bg }}>
                    <span>{slot.icon} {isExistingUrl ? "رابط محفوظ" : existing}</span>
                    <button type="button" className="il-chip-remove"
                      onClick={() => clearSlot(slot.key)}>✕</button>
                  </div>
                )}

                {/* New file */}
                {files[slot.key] && (
                  <div className="il-file-chip" style={{ borderColor: slot.color, background: slot.bg }}>
                    <span>{slot.icon} {files[slot.key].name}</span>
                    <button type="button" className="il-chip-remove"
                      onClick={() => setFiles(p => ({ ...p, [slot.key]: null }))}>✕</button>
                  </div>
                )}

                {/* Upload zone */}
                {!files[slot.key] && (!existing || cleared[slot.key]) && (
                  <label className="il-dropzone" style={{ borderColor: slot.color, cursor: "pointer" }}>
                    <span className="il-dropzone-icon">{slot.icon}</span>
                    <p>{slot.uploadHint}</p>
                    <input type="file" accept={slot.accept} style={{ display: "none" }}
                      onChange={(e) => { const f = e.target.files[0]; if (f) setFiles(p => ({ ...p, [slot.key]: f })); }} />
                  </label>
                )}

                {/* URL input */}
                {!files[slot.key] && (() => {
                  const urlErr = validateSlotUrl(slot.key, texts[slot.key]);
                  return (
                    <>
                      <input type="text"
                        className={`il-url-input${urlErr ? " il-url-error" : ""}`}
                        placeholder="أو أدخل رابط..."
                        value={texts[slot.key]}
                        onChange={(e) => setTexts(p => ({ ...p, [slot.key]: e.target.value }))}
                      />
                      {urlErr && <span className="il-url-err">{urlErr}</span>}
                    </>
                  );
                })()}
              </div>
            );
          })}
        </div>
        <div className="il-edit-footer">
          <button className="il-edit-cancel" onClick={onClose} disabled={saving}>إلغاء</button>
          <button className="il-submit-btn dash-add-btn" onClick={handleSave} disabled={saving || hasUrlError}>
            {saving ? "جاري الحفظ..." : "حفظ التعديلات"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── FileField for add form ─── */
function FileField({ slot, file, onFile, text, onText, disabled }) {
  const ref = useRef(null);
  const urlError = !file ? validateSlotUrl(slot.key, text) : null;
  return (
    <div className="il-field">
      <label className="il-label">{slot.label} <span className="il-optional">(اختياري)</span></label>
      <input ref={ref} type="file" accept={slot.accept} style={{ display: "none" }}
        onChange={(e) => onFile(e.target.files[0] || null)} disabled={disabled} />
      {file ? (
        <div className="il-file-chip" style={{ borderColor: slot.color, background: slot.bg }}>
          <span><span>{slot.icon}</span> {file.name}</span>
          <button type="button" className="il-chip-remove"
            onClick={() => { onFile(null); if (ref.current) ref.current.value = ""; }}>✕</button>
        </div>
      ) : (
        <div className="il-dropzone" onClick={() => !disabled && ref.current?.click()}
          style={disabled ? {} : { borderColor: slot.color }}>
          <span className="il-dropzone-icon">{slot.icon}</span>
          <p>{slot.uploadHint}</p>
        </div>
      )}
      {!file && (
        <>
          <input type="text"
            className={`il-url-input${urlError ? " il-url-error" : ""}`}
            placeholder="أو أدخل رابط..."
            value={text} onChange={(e) => onText(e.target.value)} disabled={disabled} />
          {urlError && <span className="il-url-err">{urlError}</span>}
        </>
      )}
    </div>
  );
}

/* ─── LectureRow ─── */
function LectureRow({ lecture, onDelete, onEdit }) {
  const [showDelete,   setShowDelete]   = useState(false);
  const [deleting,     setDeleting]     = useState(false);
  const [showEdit,     setShowEdit]     = useState(false);
  const [videoUrl,     setVideoUrl]     = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const blobUrlRef = useRef(null);

  async function confirmDelete() {
    setDeleting(true);
    try {
      await onDelete(lecture.id);
    } catch {
      setDeleting(false);
      setShowDelete(false);
    }
  }

  async function handleVideoFileClick(filename) {
    setLoadingVideo(true);
    try {
      const res = await Axios.get(ADMIN_LECTURE_INTENSIVE_FILE(filename), { responseType: "blob" });
      const mimeType = res.headers["content-type"] || "video/mp4";
      const blob = new Blob([res.data], { type: mimeType });
      const url  = window.URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setVideoUrl(url);
    } catch {
      alert("تعذر تحميل الفيديو");
    } finally {
      setLoadingVideo(false);
    }
  }

  function closeVideo() {
    setVideoUrl(null);
    if (blobUrlRef.current) {
      window.URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }

  function renderCell(slot) {
    const value = lecture[slot.key];
    if (!value) {
      return (
        <div key={slot.key} className="il-row-cell">
          <span className="il-cell-dash">—</span>
        </div>
      );
    }
    const isUrl = /^https?:\/\//i.test(value);

    if (slot.key === "video") {
      return (
        <div key={slot.key} className="il-row-cell">
          <button
            type="button"
            className="file-pill file-pill-btn"
            style={{ color: slot.color, background: slot.bg }}
            disabled={loadingVideo}
            onClick={() => isUrl ? setVideoUrl(value) : handleVideoFileClick(value)}
          >
            <span>{slot.icon}</span>
            {loadingVideo ? "..." : slot.label.replace("ملف ", "")}
          </button>
        </div>
      );
    }

    return (
      <div key={slot.key} className="il-row-cell">
        {isUrl ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="file-pill"
            style={{ color: slot.color, background: slot.bg }}
          >
            <span>{slot.icon}</span> {slot.label.replace("ملف ", "")}
          </a>
        ) : (
          <button
            type="button"
            className="file-pill file-pill-btn"
            style={{ color: slot.color, background: slot.bg }}
            onClick={() => openIntensiveFile(value)}
          >
            <span>{slot.icon}</span> {slot.label.replace("ملف ", "")}
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="il-row">
        <span className="id-badge">{lecture.id}</span>
        <div className="il-row-name">
          {lecture.name || <span className="il-cell-dash">—</span>}
        </div>
        {SLOTS.map((slot) => renderCell(slot))}
        <div className="il-row-actions">
          <button className="action-btn action-edit" onClick={() => setShowEdit(true)} title="تعديل">
            <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
            </svg>
          </button>
          <button className="action-btn action-delete" onClick={() => setShowDelete(true)} title="حذف">
            <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
            </svg>
          </button>
        </div>
      </div>

      {showDelete && (
        <DeleteModal
          onConfirm={confirmDelete}
          onCancel={() => setShowDelete(false)}
          deleting={deleting}
        />
      )}
      {showEdit && (
        <EditModal
          lecture={lecture}
          onSave={(updated) => { onEdit(updated); setShowEdit(false); }}
          onClose={() => setShowEdit(false)}
        />
      )}
      {videoUrl && (
        <VideoModal url={videoUrl} onClose={closeVideo} />
      )}
    </>
  );
}

/* ─── Main page ─── */
export default function IntensiveLectures() {
  const { id } = useParams();
  const nav = useNavigate();

  const [courseName,   setCourseName]   = useState("");
  const [lectures,     setLectures]     = useState([]);
  const [loadingList,  setLoadingList]  = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [saveErr,      setSaveErr]      = useState("");

  const [lectureName, setLectureName] = useState("");
  const [files, setFiles] = useState({ pdf: null, video: null, exam: null });
  const [texts, setTexts] = useState({ pdf: "", video: "", exam: "" });

  useEffect(() => {
    Axios.get(ADMIN_ONE_INTENSIVE(id))
      .then((res) => setCourseName(res.data.one_intensive?.name || `#${id}`))
      .catch(() => setCourseName(`#${id}`));
  }, [id]);

  function loadLectures() {
    setLoadingList(true);
    Axios.get(ADMIN_LECTURES_BY_INTENSIVE(id))
      .then((res) => setLectures(res.data.lectures || []))
      .catch(() => setLectures([]))
      .finally(() => setLoadingList(false));
  }

  useEffect(() => { loadLectures(); }, [id]);

  async function handleDeleteLecture(lectureId) {
    await Axios.delete(ADMIN_LECTURE_INTENSIVE_BY_ID(lectureId));
    setLectures((prev) => prev.filter((l) => l.id !== lectureId));
  }

  function handleEditLecture(updated) {
    setLectures((prev) => prev.map((l) => l.id === updated.id ? updated : l));
  }

  const hasAnySlot = SLOTS.some((s) => files[s.key] || texts[s.key].trim());
  const hasUrlError = SLOTS.some((s) => !files[s.key] && !!validateSlotUrl(s.key, texts[s.key]));

  async function handleSubmit() {
    if (!hasAnySlot || hasUrlError) return;
    setSaving(true);
    setSaveErr("");
    try {
      const fd = new FormData();
      fd.append("intensive_id", id);
      if (lectureName.trim()) fd.append("name", lectureName.trim());
      SLOTS.forEach((s) => {
        if (files[s.key]) fd.append(s.key, files[s.key]);
        else if (texts[s.key].trim()) fd.append(s.key, texts[s.key].trim());
      });
      await Axios.post(ADMIN_ADD_LECTURE_INTENSIVE, fd);
      setLectureName("");
      setFiles({ pdf: null, video: null, exam: null });
      setTexts({ pdf: "", video: "", exam: "" });
      loadLectures();
    } catch (err) {
      setSaveErr(err?.response?.data?.message || "حدث خطأ أثناء الحفظ");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="dash-page">
      {saving && <LoadingSubmit />}
      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">دروس الدورات المكثفة</h1>
          <p className="il-subtitle">{courseName}</p>
        </div>
        <button className="dash-add-btn" onClick={() => nav(-1)} style={{ background: "#374151" }}>
          رجوع
        </button>
      </div>

      {/* Add form */}
      <div className="table-card il-add-card">
        <h3 className="il-section-title">إضافة درس جديد</h3>
        {saveErr && <div className="il-error">{saveErr}</div>}
        <div className="il-name-field">
          <label className="il-label">اسم الدرس <span className="il-optional">(اختياري)</span></label>
          <input
            type="text"
            className="il-url-input"
            placeholder="مثال: الدرس الأول - المقدمة..."
            value={lectureName}
            onChange={(e) => setLectureName(e.target.value)}
            disabled={saving}
          />
        </div>
        <div className="il-fields-grid">
          {SLOTS.map((slot) => (
            <FileField key={slot.key} slot={slot}
              file={files[slot.key]}
              onFile={(f) => setFiles((p) => ({ ...p, [slot.key]: f }))}
              text={texts[slot.key]}
              onText={(v) => setTexts((p) => ({ ...p, [slot.key]: v }))}
              disabled={saving}
            />
          ))}
        </div>
        <button className="dash-add-btn il-submit-btn" onClick={handleSubmit}
          disabled={!hasAnySlot || hasUrlError || saving}>
          {saving ? "جاري الحفظ..." : "حفظ المحاضرة"}
        </button>
      </div>

      {/* Existing lectures */}
      <div className="table-card il-list-card">
        <h3 className="il-section-title">الدروس الحالية ({lectures.length})</h3>
        {loadingList ? (
          <p className="il-muted">جاري التحميل...</p>
        ) : lectures.length === 0 ? (
          <p className="il-muted">لا توجد دروس لهذه الدورة بعد</p>
        ) : (
          <div className="il-rows">
            <div className="il-table-header">
              <span>#</span>
              <span>اسم الدرس</span>
              <span>PDF</span>
              <span>فيديو</span>
              <span>اختبار</span>
              <span></span>
            </div>
            {lectures.map((l) => (
              <LectureRow key={l.id} lecture={l}
                onDelete={handleDeleteLecture}
                onEdit={handleEditLecture}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
